import { useState } from 'react';
import toast from 'react-hot-toast';

import { IconCheckCircle, IconClose } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import AnimalSearch from '../../animals/forms/animal-search';
import { useCreateProjectedCross, useUpdateProjectedCross } from '../contexts/projected-crosses';

import './styles/form.css';

const animalLabel = (a) => a?.name || a?.number || (a?.id ? `#${a.id}` : '—');

// Alta/edición de un cruce proyecto (admin-projected-crosses.md · 0090): plantilla
// genética donadora × toro. La donadora y el `code` son obligatorios; el TORO es
// OPCIONAL (null = "cualquier toro", cruce donor-only). En EDICIÓN donadora/toro/código
// son inmutables (solo name/capacity/notas). Se abre desde la venta o el catálogo.
export default function ProjectedCrossForm({ cross, onClose, onCreated, onSaved }) {
    const editing = !!cross;
    const { mutateAsync: create, isPending: creating } = useCreateProjectedCross();
    const { mutateAsync: update, isPending: updating } = useUpdateProjectedCross(cross?.id);
    const busy = creating || updating;
    const cardRef = useModalDialog(onClose);

    const [donor, setDonor] = useState(cross?.donor || null);
    const [sire, setSire] = useState(cross?.sire || null);
    const [code, setCode] = useState(cross?.code || '');
    const [name, setName] = useState(cross?.name || '');
    const [capacity, setCapacity] = useState(cross?.capacity != null ? String(cross.capacity) : '');
    const [remarks, setRemarks] = useState(typeof cross?.remarks === 'string' ? cross.remarks : '');

    const submit = async () => {
        if (capacity !== '' && !(Number(capacity) >= 1)) { toast.error('El cupo debe ser 1 o más (o dejalo vacío = ilimitado)'); return; }

        if (editing) {
            const payload = {
                name: name.trim() || null,
                capacity: capacity !== '' ? Number(capacity) : null,
                remarks: remarks.trim() || null,
            };
            try {
                await update(payload);
                toast.success('Cruce actualizado');
                onSaved?.();
            } catch (er) {
                if (er?.status !== 403) toast.error(er?.message || 'No se pudo actualizar el cruce');
            }
            return;
        }

        if (!donor) { toast.error('Elegí la donadora (madre)'); return; }
        if (sire && donor.id === sire.id) { toast.error('La donadora y el toro no pueden ser el mismo animal'); return; }
        if (!code.trim()) { toast.error('El código es obligatorio'); return; }

        // Toro opcional (0090): null = "cualquier toro" (cruce donor-only).
        const payload = { donor_id: donor.id, sire_animal_id: sire ? sire.id : null, code: code.trim() };
        if (name.trim()) payload.name = name.trim();
        if (capacity !== '') payload.capacity = Number(capacity);
        if (remarks.trim()) payload.remarks = remarks.trim();
        try {
            const created = await create(payload);
            toast.success('Cruce creado');
            onCreated?.(created);
            onSaved?.();
        } catch (er) {
            if (er?.status === 409) toast.error(er?.message || 'Ya existe un cruce con ese código o con esa donadora × toro');
            else if (er?.status === 422) toast.error(er?.message || 'Donadora/toro inválidos (inactivos o el mismo animal)');
            else if (er?.status !== 403) toast.error(er?.message || 'No se pudo crear el cruce');
        }
    };

    return (
        <div className="pcross-modal" role="dialog" aria-modal="true" aria-label={editing ? 'Editar cruce planeado' : 'Nuevo cruce planeado'}>
            <div className="pcross-modal__overlay" onClick={onClose} aria-hidden="true" />
            <div className="pcross-modal__card" ref={cardRef}>
                <header className="pcross-modal__head">
                    <div>
                        <h2 className="pcross-modal__title">{editing ? 'Editar cruce planeado' : 'Nuevo cruce planeado'}</h2>
                        <span className="pcross-modal__sub">Plantilla genética donadora × toro (el toro es opcional).</span>
                    </div>
                    <button type="button" className="pcross-modal__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                <div className="pcross-form">
                    <div className="pcross-form__field">
                        <span className="pcross-form__label">Donadora (madre)</span>
                        {editing ? (
                            <span className="pcross-form__readonly">{animalLabel(donor)}</span>
                        ) : donor ? (
                            <span className="pcross-form__picked pcross-form__picked--donor">
                                <IconCheckCircle /> {animalLabel(donor)}
                                <button type="button" className="pcross-form__clear" onClick={() => setDonor(null)} disabled={busy} aria-label="Cambiar donadora">×</button>
                            </span>
                        ) : (
                            <AnimalSearch onSelect={setDonor} disabled={busy} placeholder="Buscar donadora…" extra={{ sex: 'female' }} />
                        )}
                    </div>

                    <div className="pcross-form__field">
                        <span className="pcross-form__label">Toro (padre) <em className="pcross-form__hint">(opcional — vacío = cualquier toro)</em></span>
                        {editing ? (
                            <span className="pcross-form__readonly">{sire ? animalLabel(sire) : 'Cualquier toro'}</span>
                        ) : sire ? (
                            <span className="pcross-form__picked pcross-form__picked--sire">
                                <IconCheckCircle /> {animalLabel(sire)}
                                <button type="button" className="pcross-form__clear" onClick={() => setSire(null)} disabled={busy} aria-label="Quitar toro">×</button>
                            </span>
                        ) : (
                            <AnimalSearch onSelect={setSire} disabled={busy} placeholder="Buscar toro (opcional)…" extra={{ sex: 'male' }} />
                        )}
                    </div>

                    <div className="pcross-form__row">
                        <label className="pcross-form__field">
                            <span className="pcross-form__label">Código {editing && <em className="pcross-form__hint">(no editable)</em>}</span>
                            <input type="text" maxLength={40} value={code} onChange={(e) => setCode(e.target.value)} disabled={busy || editing} placeholder="Ej. PC-001" />
                        </label>
                        <label className="pcross-form__field">
                            <span className="pcross-form__label">Cupo <em className="pcross-form__hint">(vacío = ilimitado)</em></span>
                            <input type="number" min="1" step="1" value={capacity} onChange={(e) => setCapacity(e.target.value)} disabled={busy} placeholder="Ilimitado" />
                        </label>
                    </div>

                    <label className="pcross-form__field">
                        <span className="pcross-form__label">Nombre</span>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} disabled={busy} placeholder="Opcional" />
                    </label>

                    <label className="pcross-form__field">
                        <span className="pcross-form__label">Notas</span>
                        <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={busy} placeholder="Opcional" />
                    </label>
                </div>

                <footer className="pcross-modal__foot">
                    <button type="button" className="pcross-btn pcross-btn--ghost" onClick={onClose} disabled={busy}>Cancelar</button>
                    <button type="button" className="pcross-btn pcross-btn--primary" onClick={submit} disabled={busy || (!editing && (!donor || !code.trim()))}>
                        {busy ? 'Guardando…' : editing ? 'Guardar' : 'Crear cruce'}
                    </button>
                </footer>
            </div>
        </div>
    );
}
